import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Settings, Play, Pause, RefreshCw, Clock, CheckCircle, 
  AlertCircle, Store, ShoppingCart, Package, ArrowUpRight,
  Activity, Zap, Server, ChevronRight, MoreVertical, FileText, Trash2
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const API = '/api';

export default function ServicesPage() {
  const [token] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(false);
  const [services, setServices] = useState({
    marketplace_orders: { enabled: false, interval: 5, last_run: null, last_result: null, running: false },
    shopify_orders: { enabled: false, interval: 5, hours_to_fetch: 48, last_run: null, last_result: null, running: false },
    product_sync: { enabled: false, interval: 30, last_run: null, last_result: null, running: false },
  });
  const [logs, setLogs] = useState([]);
  const [syncLogs, setSyncLogs] = useState([]);
  const [stats, setStats] = useState({ total_synced: 0, pending: 0, errors: 0 });

  const fetchAllStatus = useCallback(async () => {
    try {
      const [mpRes, shopifyRes, productRes, logsRes, syncLogsRes] = await Promise.all([
        axios.get(`${API}/marketplace-orders/scheduler/status?token=${token}`).catch(() => ({ data: {} })),
        axios.get(`${API}/shopify-orders/scheduler/status?token=${token}`).catch(() => ({ data: {} })),
        axios.get(`${API}/auto-sync/status?token=${token}`).catch(() => ({ data: {} })),
        axios.get(`${API}/logs?token=${token}&limit=10`).catch(() => ({ data: { logs: [] } })),
        axios.get(`${API}/shopify-orders/sync-logs?token=${token}&limit=30`).catch(() => ({ data: { logs: [] } })),
      ]);

      setServices({
        marketplace_orders: {
          enabled: mpRes.data?.settings?.enabled || mpRes.data?.is_running || false,
          interval: mpRes.data?.settings?.pull_interval_minutes || 5,
          last_run: mpRes.data?.settings?.last_pull_time || mpRes.data?.last_pull_time,
          last_result: mpRes.data?.settings?.last_result,
          running: mpRes.data?.job_exists || false,
          next_run: mpRes.data?.next_run_time,
        },
        shopify_orders: {
          enabled: shopifyRes.data?.settings?.enabled || shopifyRes.data?.is_running || false,
          interval: shopifyRes.data?.settings?.pull_interval_minutes || 5,
          hours_to_fetch: shopifyRes.data?.hours_to_fetch || shopifyRes.data?.settings?.hours_to_fetch || 48,
          last_run: shopifyRes.data?.settings?.last_pull_time || shopifyRes.data?.last_pull_time,
          last_result: shopifyRes.data?.settings?.last_result,
          running: shopifyRes.data?.job_exists || false,
          next_run: shopifyRes.data?.next_run_time,
          last_customer_sync_result: shopifyRes.data?.last_customer_sync_result,
          last_orders_fetch_count: shopifyRes.data?.last_orders_fetch_count,
        },
        product_sync: {
          enabled: productRes.data?.enabled || false,
          interval: productRes.data?.sync_interval_minutes || 30,
          last_run: productRes.data?.last_sync_time,
          last_result: productRes.data?.last_result,
          running: productRes.data?.job_exists || false,
          next_run: productRes.data?.next_run_time,
          last_sync_detail: productRes.data?.last_sync_detail || '',
        },
      });

      setLogs(logsRes.data?.logs || []);
      setSyncLogs(syncLogsRes.data?.logs || []);
    } catch (error) {
      console.error('Status fetch error:', error);
    }
  }, [token]);

  useEffect(() => {
    fetchAllStatus();
    const interval = setInterval(fetchAllStatus, 30000); // Her 30 saniyede güncelle
    return () => clearInterval(interval);
  }, [fetchAllStatus]);

  const toggleService = async (serviceKey, enabled) => {
    setLoading(true);
    try {
      let endpoint = '';
      let interval = services[serviceKey].interval;
      let hoursToFetch = services[serviceKey].hours_to_fetch || 48;
      
      switch(serviceKey) {
        case 'marketplace_orders':
          // Marketplace endpoint body bekliyor
          endpoint = `/marketplace-orders/scheduler/settings?token=${token}`;
          await axios.put(`${API}${endpoint}`, { 
            enabled, 
            pull_interval_minutes: interval,
            auto_sync_to_shopify: services[serviceKey].auto_sync_to_shopify || false,
            marketplaces: [],
            pull_order_statuses: ["pending", "processing"]
          });
          toast.success(enabled ? 'Servis başlatıldı' : 'Servis durduruldu');
          fetchAllStatus();
          setLoading(false);
          return;
        case 'shopify_orders':
          endpoint = `/shopify-orders/scheduler/settings?token=${token}&enabled=${enabled}&pull_interval_minutes=${interval}&hours_to_fetch=${hoursToFetch}`;
          break;
        case 'product_sync':
          endpoint = `/auto-sync/settings?token=${token}&enabled=${enabled}&sync_interval_minutes=${interval}`;
          await axios.put(`${API}${endpoint}`, { enabled, sync_interval_minutes: interval });
          toast.success(enabled ? 'Servis başlatıldı' : 'Servis durduruldu');
          fetchAllStatus();
          setLoading(false);
          return;
      }
      
      await axios.put(`${API}${endpoint}`);
      toast.success(enabled ? 'Servis başlatıldı' : 'Servis durduruldu');
      fetchAllStatus();
    } catch (error) {
      console.error('Toggle service error:', error);
      toast.error('İşlem başarısız');
    } finally {
      setLoading(false);
    }
  };

  const updateInterval = async (serviceKey, interval) => {
    setLoading(true);
    try {
      let endpoint = '';
      const enabled = services[serviceKey].enabled;
      const hoursToFetch = services[serviceKey].hours_to_fetch || 48;
      
      switch(serviceKey) {
        case 'marketplace_orders':
          // Marketplace endpoint body bekliyor
          endpoint = `/marketplace-orders/scheduler/settings?token=${token}`;
          await axios.put(`${API}${endpoint}`, { 
            enabled, 
            pull_interval_minutes: parseInt(interval),
            auto_sync_to_shopify: services[serviceKey].auto_sync_to_shopify || false,
            marketplaces: [],
            pull_order_statuses: ["pending", "processing"]
          });
          toast.success('Aralık güncellendi');
          fetchAllStatus();
          setLoading(false);
          return;
        case 'shopify_orders':
          endpoint = `/shopify-orders/scheduler/settings?token=${token}&enabled=${enabled}&pull_interval_minutes=${interval}&hours_to_fetch=${hoursToFetch}`;
          break;
        case 'product_sync':
          endpoint = `/auto-sync/settings?token=${token}&enabled=${enabled}&sync_interval_minutes=${parseInt(interval)}`;
          await axios.put(`${API}${endpoint}`, { enabled, sync_interval_minutes: parseInt(interval) });
          toast.success('Aralık güncellendi');
          fetchAllStatus();
          setLoading(false);
          return;
      }
      
      await axios.put(`${API}${endpoint}`);
      toast.success('Aralık güncellendi');
      fetchAllStatus();
    } catch (error) {
      console.error('Update interval error:', error);
      toast.error('Güncelleme başarısız');
    } finally {
      setLoading(false);
    }
  };

  const updateHoursToFetch = async (serviceKey, hours) => {
    setLoading(true);
    try {
      const enabled = services[serviceKey].enabled;
      const interval = services[serviceKey].interval;
      
      const endpoint = `/shopify-orders/scheduler/settings?token=${token}&enabled=${enabled}&pull_interval_minutes=${interval}&hours_to_fetch=${hours}`;
      await axios.put(`${API}${endpoint}`);
      toast.success(`Son ${hours} saat olarak güncellendi`);
      fetchAllStatus();
    } catch (error) {
      toast.error('Güncelleme başarısız');
    } finally {
      setLoading(false);
    }
  };

  const runNow = async (serviceKey) => {
    setLoading(true);
    try {
      let endpoint = '';
      
      switch(serviceKey) {
        case 'marketplace_orders':
          endpoint = `/marketplace-orders/scheduler/run-now?token=${token}`;
          break;
        case 'shopify_orders':
          endpoint = `/shopify-orders/scheduler/run-now?token=${token}`;
          break;
        case 'product_sync':
          endpoint = `/auto-sync/run-now?token=${token}`;
          break;
      }
      
      toast.info('Servis çalıştırılıyor...');
      await axios.post(`${API}${endpoint}`, {}, { timeout: 300000 });
      toast.success('İşlem tamamlandı');
      fetchAllStatus();
    } catch (error) {
      toast.error('İşlem başarısız');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000 / 60); // dakika farkı
    
    if (diff < 1) return 'Az önce';
    if (diff < 60) return `${diff} dk önce`;
    if (diff < 1440) return `${Math.floor(diff / 60)} saat önce`;
    
    return date.toLocaleString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const [stoppingAll, setStoppingAll] = useState(false);

  const stopAllServices = async () => {
    if (!window.confirm('TÜM aktif servisleri ve devam eden aktarımları durdurmak istediğinize emin misiniz?')) return;
    setStoppingAll(true);
    
    // Frontend aktarım döngülerini durdur
    localStorage.setItem("cancel_sync", "true");
    
    try {
      const res = await axios.post(`${API}/services/stop-all?token=${token}`);
      const count = res.data.stopped_services?.length || 0;
      toast.success(`${count} servis durduruldu, aktarımlar iptal edildi`);
      fetchAllStatus();
    } catch (error) {
      toast.error('Servisler durdurulamadı');
    } finally {
      setStoppingAll(false);
    }
  };

  const serviceConfig = [
    {
      key: 'marketplace_orders',
      name: 'Pazaryeri Sipariş Sync',
      description: 'Trendyol, N11, Çiçeksepeti siparişlerini çeker ve Shopify\'a aktarır',
      icon: ShoppingCart,
      color: 'purple',
    },
    {
      key: 'shopify_orders',
      name: 'Shopify Sipariş Çekme',
      description: 'Siparişleri çeker → Nebim\'e aktarır → Müşteri sync → Sipariş sonuçları',
      icon: Store,
      color: 'green',
    },
    {
      key: 'product_sync',
      name: 'Ürün Senkronizasyonu',
      description: 'Nebim ürünlerini Shopify\'a senkronize eder',
      icon: Package,
      color: 'blue',
    },
  ];

  const getStatusBadge = (service) => {
    if (service.running && service.enabled) {
      return <Badge className="bg-green-100 text-green-700 border-green-200">Aktif</Badge>;
    }
    if (service.enabled && !service.running) {
      return <Badge className="bg-yellow-100 text-yellow-700 border-yellow-200">Beklemede</Badge>;
    }
    return <Badge variant="outline" className="text-slate-500">Kapalı</Badge>;
  };

  return (
    <div className="p-6 space-y-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Server className="w-7 h-7 text-indigo-600" />
            Servis Yönetimi
          </h1>
          <p className="text-slate-500">Tüm otomasyon servislerini buradan yönetin</p>
        </div>
        
        <div className="flex items-center gap-3">
          <Button
            variant="destructive"
            size="sm"
            onClick={stopAllServices}
            disabled={stoppingAll}
            data-testid="btn-stop-all-services"
          >
            {stoppingAll ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Pause className="w-4 h-4 mr-2" />}
            Tüm Servisleri Durdur
          </Button>
          <Button variant="outline" onClick={fetchAllStatus} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Yenile
          </Button>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-600 font-medium">Aktif Servisler</p>
                <p className="text-3xl font-bold text-green-700">
                  {Object.values(services).filter(s => s.enabled && s.running).length}
                  <span className="text-lg text-green-500">/{Object.keys(services).length}</span>
                </p>
              </div>
              <Activity className="w-10 h-10 text-green-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-600 font-medium">Son 24 Saat</p>
                <p className="text-3xl font-bold text-blue-700">
                  {logs.filter(l => {
                    const logDate = new Date(l.timestamp);
                    const now = new Date();
                    return (now - logDate) < 24 * 60 * 60 * 1000;
                  }).length} <span className="text-lg text-blue-500">işlem</span>
                </p>
              </div>
              <Zap className="w-10 h-10 text-blue-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-orange-50 to-amber-50 border-orange-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-orange-600 font-medium">Sonraki Çalışma</p>
                <p className="text-xl font-bold text-orange-700">
                  {(() => {
                    const nextRuns = Object.values(services)
                      .filter(s => s.next_run)
                      .map(s => new Date(s.next_run));
                    if (nextRuns.length === 0) return '-';
                    const next = new Date(Math.min(...nextRuns));
                    const diff = Math.floor((next - new Date()) / 1000 / 60);
                    if (diff < 1) return 'Şimdi';
                    return `${diff} dk sonra`;
                  })()}
                </p>
              </div>
              <Clock className="w-10 h-10 text-orange-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Services List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Servisler
          </CardTitle>
          <CardDescription>
            Servisleri başlatın, durdurun ve ayarlarını yapılandırın
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {serviceConfig.map(config => {
            const service = services[config.key];
            const Icon = config.icon;
            
            return (
              <div 
                key={config.key}
                className={`p-4 rounded-xl border-2 transition-all overflow-hidden ${
                  service.enabled && service.running 
                    ? `border-${config.color}-200 bg-${config.color}-50/30` 
                    : 'border-slate-200 bg-slate-50/50'
                }`}
              >
                <div className="flex items-center justify-between gap-3">
                  {/* Sol: Icon ve Bilgi */}
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className={`p-2.5 rounded-xl flex-shrink-0 ${
                      service.enabled && service.running
                        ? `bg-${config.color}-100`
                        : 'bg-slate-100'
                    }`}>
                      <Icon className={`w-5 h-5 ${
                        service.enabled && service.running
                          ? `text-${config.color}-600`
                          : 'text-slate-400'
                      }`} />
                    </div>
                    
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <h3 className="font-semibold text-slate-800 text-sm">{config.name}</h3>
                        {getStatusBadge(service)}
                      </div>
                      <p className="text-xs text-slate-500 truncate">{config.description}</p>
                    </div>
                  </div>
                  
                  {/* Orta: Durum Bilgileri */}
                  <div className="hidden lg:flex items-center gap-4 text-sm flex-shrink-0">
                    <div className="text-center w-20">
                      <p className="text-slate-400 text-xs">Son Çalışma</p>
                      <p className="font-medium text-slate-700 text-xs truncate" title={service.last_run ? new Date(service.last_run).toLocaleString('tr-TR') : '-'}>
                        {service.last_run ? new Date(service.last_run).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '-'}
                      </p>
                      {service.last_run && (
                        <p className="text-slate-400 text-[10px]">{formatDate(service.last_run)}</p>
                      )}
                    </div>
                    <div className="text-center">
                      <p className="text-slate-400 text-xs">Aralık</p>
                      <Select 
                        value={String(service.interval)} 
                        onValueChange={(v) => updateInterval(config.key, v)}
                        disabled={loading}
                      >
                        <SelectTrigger className="w-20 h-7 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1">1 dk</SelectItem>
                          <SelectItem value="5">5 dk</SelectItem>
                          <SelectItem value="10">10 dk</SelectItem>
                          <SelectItem value="15">15 dk</SelectItem>
                          <SelectItem value="30">30 dk</SelectItem>
                          <SelectItem value="60">1 saat</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {config.key === 'shopify_orders' && (
                      <div className="text-center">
                        <p className="text-slate-400 text-xs">Son</p>
                        <Select 
                          value={String(service.hours_to_fetch || 48)} 
                          onValueChange={(v) => updateHoursToFetch(config.key, v)}
                          disabled={loading}
                        >
                          <SelectTrigger className="w-20 h-7 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="6">6 saat</SelectItem>
                            <SelectItem value="12">12 saat</SelectItem>
                            <SelectItem value="24">24 saat</SelectItem>
                            <SelectItem value="48">48 saat</SelectItem>
                            <SelectItem value="72">72 saat</SelectItem>
                            <SelectItem value="168">1 hafta</SelectItem>
                            <SelectItem value="336">2 hafta</SelectItem>
                            <SelectItem value="720">30 gün</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    {service.last_result && (
                      <div className="text-center w-28 overflow-hidden">
                        <p className="text-slate-400 text-xs">Son Sonuç</p>
                        <p className="font-medium text-slate-700 truncate text-xs" title={service.last_result}>{service.last_result}</p>
                      </div>
                    )}
                  </div>
                  
                  {/* Sag: Aksiyonlar */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => runNow(config.key)}
                      disabled={loading}
                      className="hidden sm:flex h-8 w-8 p-0"
                    >
                      <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                    </Button>
                    
                    <Switch
                      checked={service.enabled}
                      onCheckedChange={(checked) => toggleService(config.key, checked)}
                      disabled={loading}
                    />
                    
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="sm:hidden h-8 w-8 p-0">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => runNow(config.key)}>
                          <RefreshCw className="w-4 h-4 mr-2" />
                          Simdi Calistir
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
                
                {/* Detayli Sync Raporu - Kart altinda */}
                {config.key === 'product_sync' && service.last_sync_detail && (
                  <div className="mt-3 pt-3 border-t border-slate-200">
                    <details>
                      <summary className="text-sm text-slate-600 cursor-pointer font-semibold flex items-center gap-1.5" data-testid="sync-detail-toggle">
                        <FileText className="w-4 h-4" />
                        Detayli Sync Raporu
                      </summary>
                      <div className="mt-2 bg-slate-900 rounded-lg p-3 max-h-[600px] overflow-y-auto font-mono text-xs" data-testid="sync-detail-content">
                        {service.last_sync_detail.split('\n').map((line, li) => {
                          let cls = 'text-slate-300';
                          if (line.includes('ISTEK:')) cls = 'text-cyan-400';
                          else if (line.includes('BODY:')) cls = 'text-slate-500';
                          else if (line.includes('CEVAP:') && (line.includes('200') || line.includes('201'))) cls = 'text-green-400';
                          else if (line.includes('CEVAP:')) cls = 'text-yellow-300';
                          else if (line.includes('HATA')) cls = 'text-red-400';
                          else if (line.includes('OZET:') || line.includes('===')) cls = 'text-yellow-300 font-bold';
                          else if (line.includes('ADIM') || line.includes('---') || line.includes('==========')) cls = 'text-blue-300 font-semibold';
                          else if (line.includes('ATLA') || line.includes('Bulunamayan')) cls = 'text-orange-400';
                          else if (line.match(/^\s*\d+\./)) cls = 'text-slate-400';
                          else if (line.includes('[Urun')) cls = 'text-purple-300';
                          return (
                            <div key={li} className={`${cls} leading-5 py-0.5 hover:bg-slate-800 px-1 rounded`}>
                              {line || '\u00A0'}
                            </div>
                          );
                        })}
                      </div>
                    </details>
                  </div>
                )}
                
                {/* Mobil: Ek Bilgiler */}
                <div className="md:hidden mt-3 pt-3 border-t border-slate-200 flex items-center justify-between text-xs text-slate-500">
                  <span>Son: {formatDate(service.last_run)}</span>
                  <span>Her {service.interval} dk</span>
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Son İşlemler */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="w-5 h-5" />
            Son İşlemler
          </CardTitle>
        </CardHeader>
        <CardContent>
          {logs.length === 0 ? (
            <p className="text-center text-slate-400 py-8">Henüz işlem yok</p>
          ) : (
            <div className="space-y-3">
              {logs.slice(0, 8).map((log, i) => (
                <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors">
                  <div className={`p-1.5 rounded-full ${
                    log.log_type === 'success' ? 'bg-green-100' :
                    log.log_type === 'error' ? 'bg-red-100' : 'bg-blue-100'
                  }`}>
                    {log.log_type === 'success' ? (
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    ) : log.log_type === 'error' ? (
                      <AlertCircle className="w-4 h-4 text-red-600" />
                    ) : (
                      <Activity className="w-4 h-4 text-blue-600" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-700">{log.message}</p>
                    <p className="text-xs text-slate-400">{formatDate(log.timestamp)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Sync Logları */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Sync Logları
            </CardTitle>
            <Badge variant="outline">{syncLogs.length} kayıt</Badge>
          </div>
          <CardDescription>Shopify-Nebim senkronizasyon işlemleri</CardDescription>
        </CardHeader>
        <CardContent>
          {syncLogs.length === 0 ? (
            <p className="text-center text-slate-400 py-8">Henüz sync log kaydı yok</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left">
                    <th className="pb-2 font-medium">Durum</th>
                    <th className="pb-2 font-medium">İşlem</th>
                    <th className="pb-2 font-medium">Mesaj</th>
                    <th className="pb-2 font-medium">Tarih</th>
                  </tr>
                </thead>
                <tbody>
                  {syncLogs.slice(0, 20).map((log, i) => (
                    <tr key={i} className="border-b hover:bg-slate-50">
                      <td className="py-2">
                        {log.status === 'success' ? (
                          <Badge className="bg-green-100 text-green-700">Başarılı</Badge>
                        ) : log.status === 'error' ? (
                          <Badge className="bg-red-100 text-red-700">Hata</Badge>
                        ) : (
                          <Badge className="bg-blue-100 text-blue-700">Bilgi</Badge>
                        )}
                      </td>
                      <td className="py-2 font-mono text-xs">{log.action}</td>
                      <td className="py-2 text-slate-600">
                        {log.details?.message || '-'}
                        {log.error && <span className="text-red-500 ml-2">({log.error})</span>}
                      </td>
                      <td className="py-2 text-slate-400 text-xs whitespace-nowrap">
                        {formatDate(log.created_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
